import { EditorCtx, UICtx } from '@frontend/editor/external-providers';
import {
  BlockTypesEnum,
  DrawerTypesEnum,
  ElementTypesEnum,
} from '@frontend/editor/interface';
import { isValueInEnum } from '@frontend/editor/utils';
import { useCallback, useContext } from 'react';
import { useReactFlow, useStoreApi, useViewport } from 'reactflow';

export interface UseCanvasView {
  onFocusID: string;
  onFocusCellID: string;
  onHoverCellID: string;
  onHoverLayer: number;
  focusNode: (nodeId: string, zoom?: number, duration?: number) => void;
  // 尋找 reactflow 內的 node id 並且 focus，如果找不到則回傳 false，代表沒有資料尚未被渲染
  focusNodeByReactFlowID: (nodeId: string) => boolean;
  focusEntryPoint: (zoom?: number) => void;
  focusOnCell: (id: string) => void;
  onHoverElement: (id: string, layer: number) => void;
  setAllowCanvasMove: (isAllow: boolean) => void;
  handleDrawer: (drawerType: DrawerTypesEnum) => void;
}

const drawerWidth = 360;

// 操作畫布
export function useCanvasView(): UseCanvasView {
  const {
    elements,
    onFocusID,
    onFocusCellID,
    getElement,
    setIsFlowDraggable,
    setIsFlowZoomOnScroll,
    setOnFocusCellID,
    setIsFlowPreventScrolling,
  } = useContext(EditorCtx);
  const {
    hoveredElement,
    setDrawerType,
    onHoverElement: setHoveredElement,
  } = useContext(UICtx);
  const store = useStoreApi();
  const { setCenter } = useReactFlow();
  const { zoom, x: viewportX } = useViewport();

  const focusNode = useCallback(
    (nodeID: string, _zoom?: number, duration = 1000) => {
      let targetNodeID = nodeID;
      const element = getElement(nodeID);
      if (element && !isValueInEnum(element.elementType, BlockTypesEnum)) {
        targetNodeID = element.nodeID as string;
      }
      const { nodeInternals } = store.getState();
      const nodes = Array.from(nodeInternals).map(([, node]) => node);
      const node = nodes.find((i) => i.id === targetNodeID);
      if (node) {
        const width = node.width ? node.width / 2 : 0;
        const height = node.height ? node.height / 2 : 0;
        const x = node.position.x + width;
        const y = node.position.y + height;

        setCenter(x, y, { zoom: _zoom || zoom, duration });
      }
    },
    [getElement, store, setCenter, zoom],
  );

  const focusNodeByReactFlowID = useCallback(
    (targetNodeID: string) => {
      const { nodeInternals } = store.getState();
      const nodes = Array.from(nodeInternals).map(([, node]) => node);
      const node = nodes.find((i) => i.id === targetNodeID);

      if (node) {
        const width = node.width ? node.width / 2 : 0;
        const height = node.height ? node.height / 2 : 0;
        const x = node.position.x + width;
        const y = node.position.y + height;

        setCenter(x, y, { zoom, duration: 1000 });
        return true;
      } else {
        return false;
      }
    },
    [store, setCenter, zoom],
  );

  const focusEntryPoint = useCallback(
    (_zoom?: number) => {
      const target = elements.find(
        (el) => el.elementType === ElementTypesEnum.ENTRY_POINT,
      );
      if (target) {
        const { nodeInternals } = store.getState();
        const nodes = Array.from(nodeInternals).map(([, node]) => node);
        const node = nodes.find((i) => i.id === target.id);
        if (node) {
          const width = node.width ? node.width / 2 : 0;
          const height = node.height ? node.height / 2 : 0;
          const x = node.position.x + width;
          const y = node.position.y + height;

          setCenter(x, y, { zoom: _zoom || zoom, duration: 1000 });
        }
      }
    },
    [elements, store, setCenter, zoom],
  );

  const handleFlowDraggable = useCallback(
    (isDrag: boolean) => {
      setIsFlowDraggable(isDrag);
    },
    [setIsFlowDraggable],
  );
  const handleFlowPreventScrolling = useCallback(
    (isPrevent: boolean) => {
      setIsFlowPreventScrolling(isPrevent);
    },
    [setIsFlowPreventScrolling],
  );

  const handleFlowZoomOnScroll = useCallback(
    (isZoom: boolean) => {
      setIsFlowZoomOnScroll(isZoom);
    },
    [setIsFlowZoomOnScroll],
  );

  const setAllowCanvasMove = useCallback(
    (isAllow: boolean) => {
      handleFlowDraggable(isAllow);
      handleFlowPreventScrolling(isAllow);
      handleFlowZoomOnScroll(isAllow);
    },
    [handleFlowDraggable, handleFlowPreventScrolling, handleFlowZoomOnScroll],
  );

  const focusOnCell = useCallback(
    (id: string) => {
      setOnFocusCellID(id);
    },
    [setOnFocusCellID],
  );

  const handleDrawer = useCallback(
    (drawerType: DrawerTypesEnum) => {
      const { nodeInternals } = store.getState();
      const nodes = Array.from(nodeInternals).map(([, node]) => node);
      const node = nodes.find((i) => i.id === onFocusID);

      if (node) {
        // 如果開啟的 drawer 會蓋住目標 element 則移動畫面中心至可以顯示的位置，避免被開啟的 drawer 蓋住
        if (node.position.x + viewportX / zoom < drawerWidth / zoom) {
          const width = node.width ? node.width / 2 : 0;
          const height = node.height ? node.height / 2 : 0;
          // 移動到畫面中心稍微偏向右邊
          const x = node.position.x + width - 200 / zoom;
          const y = node.position.y + height;

          setCenter(x, y, { zoom, duration: 1000 });
        }
      }

      setDrawerType(drawerType);
    },
    [onFocusID, setCenter, setDrawerType, store, viewportX, zoom],
  );

  // WHY 不再在 hover 到 Element 時關閉 nodesDraggable：
  // 這條規則原本是避免拖節點干擾區塊內元件排序，但元件排序早已改用專用把手
  // (SortableList useDragHandle)，規則只剩副作用——節點本體整塊都拖不動，標題列又被名稱輸入框
  // (nodrag) 佔滿，使用者實際能拖的只剩左上角小圖示；節點被別的節點蓋住時就完全搬不走
  // （追蹤解鎖內容模板 20+ 客訴）。文字輸入區仍靠自身的 nodrag 保護選字行為。
  const onHoverElement = useCallback(
    (id: string, layer: number) => {
      setHoveredElement(id, layer);
    },
    [setHoveredElement],
  );

  return {
    onFocusID,
    onFocusCellID: onFocusCellID,
    onHoverCellID: hoveredElement.id,
    onHoverLayer: hoveredElement.layer,
    focusOnCell,
    focusEntryPoint,
    focusNode,
    setAllowCanvasMove,
    focusNodeByReactFlowID,
    onHoverElement,
    handleDrawer,
  };
}

export default useCanvasView;
